import {
  ComplianceEntitySubjectType,
  ComplianceEntityType,
  GetMissingFieldsLevelParameter,
  SubjectType
} from "@wingspanhq/wingspan-api-sdk";
import { TinType } from "@wingspanhq/payments/dist/interfaces/taxForm";
import { complianceEntitiesApi, payeesApi } from "@api";
import { queriesComplianceEntities, queriesPayeeRows } from "@query";
import { useWSMutation, WSMutationConfig } from "../../../query/helpers";
import { WSServiceError } from "../../../utils/serviceHelper";
import { QUERY_PAYEE } from "../../../query/payee/keys";
import { QUERY_PAYEE_ROWS } from "../../../query/search/payee/queries/useQueryPayeeRows";
import { selectorCounterpartyProfileWrite } from "../selectors/selectorCounterpartyProfileWrite";
import { selectorComplianceEntityFormAndClassification } from "../selectors/selectorComplianceEntityFormAndClassification";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";

/**
 * v3 compliance/forms migration of the payer-supplied "Edit contractor tax info"
 * flow (v1 `payeeW9Data` via `PATCH /payee/:id`). The payer's copy of a
 * contractor's W-9 identity + TIN verification now lives on a payer-owned
 * `ComplianceEntity` with `subject = { Payee, payeeId }` (the design's "Path 1").
 * Field mappings are kept 1:1 with the contractor-onboarding create flow
 * (`Onboarding/steps/CreateAccount/useMutationSubmit.ts`).
 *
 * INTERIM — built against the SDK + the v3 proposal (`.md`), not runtime-verified
 * (tax PII); needs smoke-testing that payer-owned subject-CE create + verify(Tax)
 * runs the IRS TIN match. Known limits: `legalName`/`type`/`taxClassification`
 * are create-only on the CE, so editing the legal business name or switching
 * SSN↔EIN on an existing entity is not reflected by the update path; and existing
 * contractors created before this flow have no CE to backfill (first save mints
 * one). See V3_MIGRATION_TRACKER.md (payees).
 */
export type ContractorTaxFormValues = {
  name?: string;
  companyName?: string;
  tinType?: TinType | `${TinType}`;
  individual: {
    firstName?: string;
    lastName?: string;
    ssn?: string;
  };
  business: {
    legalBusinessName?: string;
    disregardedEntityName?: string;
    ein?: string;
    companyStructure?: string;
  };
  address: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
};

/**
 * The `listComplianceEntities` request selecting the payer-owned compliance
 * entity whose subject is this payee — the single source of the filter, reused
 * by both the hook below and the Requirements tax-verification batch fetch.
 */
export const getContractorComplianceEntityListRequest = (payeeId: string) => ({
  filter: {
    subjectType: { eq: SubjectType.PAYEE },
    subjectId: { eq: payeeId }
  }
});

/** The payer-owned compliance entity for a contractor (subject = the payee). */
export const useQueryContractorComplianceEntity = (payeeId?: string) =>
  queriesComplianceEntities.listComplianceEntities.useQuery(
    getContractorComplianceEntityListRequest(payeeId ?? ""),
    {
      enabled: !!payeeId,
      select: list => list.data?.[0]
    }
  );

type SaveContractorTaxInfoInput = {
  payeeId: string;
  addTaxInfo: boolean;
  /** The existing payer-owned CE id for this payee, if one exists. */
  complianceEntityId?: string;
  formData: ContractorTaxFormValues;
};

export const useSaveContractorTaxInfo = (
  config?: WSMutationConfig<void, WSServiceError, SaveContractorTaxInfoInput>
) => {
  const xWingspanAccount = useXWingspanPayerId();

  return useWSMutation<void, WSServiceError, SaveContractorTaxInfoInput>(
    async ({ payeeId, addTaxInfo, complianceEntityId, formData }) => {
      const isBusiness = formData.tinType === TinType.Business;

      // Name/company → the v3 Payee profile (display identity).
      await payeesApi.updatePayee({
        payeeId,
        payeeUpdate: {
          profile: selectorCounterpartyProfileWrite(
            addTaxInfo
              ? isBusiness
                ? { company: formData.business.legalBusinessName }
                : {
                    name: `${formData.individual.firstName ?? ""} ${
                      formData.individual.lastName ?? ""
                    }`.trim()
                  }
              : { name: formData.name, company: formData.companyName }
          )
        },
        xWingspanAccount
      });

      if (!addTaxInfo) {
        return;
      }

      // W-9 tax identity → a payer-owned ComplianceEntity (subject = the payee).
      const physicalAddress = {
        line1: formData.address.addressLine1 || undefined,
        line2: formData.address.addressLine2 || undefined,
        city: formData.address.city || undefined,
        state: formData.address.state || undefined,
        postalCode: formData.address.postalCode || undefined,
        country: formData.address.country || "US"
      };

      const taxIdentifiers = isBusiness
        ? formData.business.ein
          ? [{ countryCode: "US", type: "EIN", value: formData.business.ein }]
          : undefined
        : formData.individual.ssn
          ? [{ countryCode: "US", type: "SSN", value: formData.individual.ssn }]
          : undefined;

      let entityId = complianceEntityId;

      if (entityId) {
        // legalName / type / taxClassification are create-only on the CE — only
        // the mutable identity fields are updated here.
        await complianceEntitiesApi.updateComplianceEntity({
          complianceEntityId: entityId,
          complianceEntityUpdate: isBusiness
            ? {
                doingBusinessAs:
                  formData.business.disregardedEntityName || undefined,
                physicalAddress,
                taxIdentifiers
              }
            : {
                individualLegalName: {
                  givenName: formData.individual.firstName || undefined,
                  familyName: formData.individual.lastName || undefined
                },
                physicalAddress,
                taxIdentifiers
              },
          xWingspanAccount
        });
      } else {
        const { form, taxClassification } =
          selectorComplianceEntityFormAndClassification(
            formData.business.companyStructure
          );

        const created = await complianceEntitiesApi.createComplianceEntity({
          complianceEntityCreate: {
            subject: { type: ComplianceEntitySubjectType.PAYEE, id: payeeId },
            type: isBusiness
              ? ComplianceEntityType.BUSINESS
              : ComplianceEntityType.INDIVIDUAL,
            jurisdictionCountry: "US",
            physicalAddress,
            taxIdentifiers,
            ...(isBusiness
              ? {
                  form,
                  taxClassification,
                  legalName: formData.business.legalBusinessName || undefined,
                  doingBusinessAs:
                    formData.business.disregardedEntityName || undefined
                }
              : {
                  individualLegalName: {
                    givenName: formData.individual.firstName || undefined,
                    familyName: formData.individual.lastName || undefined
                  }
                })
          },
          xWingspanAccount
        });

        entityId = created.id;
      }

      // Run the IRS TIN match (the v1 verification side effect made explicit).
      await complianceEntitiesApi.verifyComplianceEntity({
        complianceEntityId: entityId,
        complianceEntityVerifyRequest: {
          level: GetMissingFieldsLevelParameter.TAX
        },
        xWingspanAccount
      });
    },
    {
      dependencies: [
        queriesComplianceEntities.listComplianceEntities.queryKeyBase,
        queriesComplianceEntities.getComplianceEntity.queryKeyBase,
        QUERY_PAYEE,
        QUERY_PAYEE_ROWS,
        queriesPayeeRows.searchPayeeRows.queryKeyBase
      ],
      ...config
    }
  );
};
